import { FunctionComponent, useRef, useState, useEffect } from "react";
import styles from "./VerifyOTP.module.css";
import cn from "classnames";
import { Form } from "react-bootstrap";
import AuthLayout from "../../../component/common/Layout/AuthLayout/AuthLayout";
import { PageRoutes } from "../../../routing/paths";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { verifyOtp, resendOtp } from "../../../redux/slices/auth/actions";
import { AppDispatch, RootState } from "../../../redux/store";
import LocalStorageService, {LocalStorageKeys} from "../../../services/localStorageService";
import { toast } from "react-toastify";
import { clearMessage, clearError } from "../../../redux/slices/auth/slice";

interface IVerifyOTPProps {
  length?: number;
}

const VerifyOTP: FunctionComponent<IVerifyOTPProps> = ({ length = 6 }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { loading, error, message, isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  const OTP_DURATION = 5 * 60; // 300 seconds
  const inputsRef = useRef<HTMLInputElement[]>([]);
  const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
  const [timer, setTimer] = useState(OTP_DURATION);
  const [isTimerActive, setIsTimerActive] = useState(true);

  const email = location.state?.email;
  const isLoginPending = LocalStorageService.get<boolean>(LocalStorageKeys.IS_LOGIN_PENDING);

  useEffect(() => {
    if (!isTimerActive) return;

    const interval = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 1) {
          clearInterval(interval);
          setIsTimerActive(false);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isTimerActive]);

  const minutes = Math.floor(timer / 60);
  const seconds = timer % 60;

  useEffect(() => {
    if (isAuthenticated) {
      if (user?.reset_password) {
        navigate(PageRoutes.RESETPASSWORD, { replace: true, state: { email } });
      } else{
        navigate(PageRoutes.DASHBOARD);
      }
    }
  }, [isAuthenticated, user, navigate, email]);

  useEffect(() => {
    return () => {
      if (!isAuthenticated && isLoginPending) {
        LocalStorageService.remove(LocalStorageKeys.IS_LOGIN_PENDING);
      }
    };
  }, [isAuthenticated, isLoginPending]);

  useEffect(() => {
    if (message) {
      toast.success(message);
      dispatch(clearMessage());
    }
  }, [message, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return;

    const updatedOtp = [...otp];
    updatedOtp[index] = value;
    setOtp(updatedOtp);
    if (value && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").slice(0, length);
    if (!/^\d+$/.test(pasted)) return;
    const updated = pasted.split("");
    const newOtp = [...otp];
    for (let i = 0; i < length; i++) {
      newOtp[i] = updated[i] || "";
    }
    setOtp(newOtp);
    inputsRef.current[pasted.length - 1]?.focus();
  };

  const handleSubmit = () => {
    if (!email) {
      // Handle case where email is not available
      return;
    }
    dispatch(verifyOtp({ email, otp: otp.join("") }));
  };

  const handleResend = () => {
    if (!email) {
      // Handle case where email is not available
      return;
    }
    dispatch(resendOtp({ email }));
    setIsTimerActive(true);
    setTimer(OTP_DURATION);
  };

  return (
    <AuthLayout>
      <div className={styles.header}>OTP verification</div>
      <div className={styles.otpText}>
        We’ve sent a 6 digit code to {email || "your email"}. <br /> Not your email?{" "}
        <Link to={PageRoutes.LOGIN} className={styles.link}>
          Change it
        </Link>
      </div>
      <div className={styles.resendCodeHeader}>
        <div className={styles.resendCodeLabel}>Enter Code</div>
        {isTimerActive && (
          <div className={styles.resendCodeTimer}>
            Expires in {`${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`}
          </div>
        )}
      </div>
      <div className={styles.verifyformOut}>
        <div className={styles.verifyform}>
          {otp.map((digit, i) => (
            <Form.Control
              ref={(el: HTMLInputElement | null) => {
                if (el) {
                  inputsRef.current[i] = el;
                }
              }}
              key={i}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(e.target.value, i)}
              onKeyDown={(e) =>
                handleKeyDown(e as React.KeyboardEvent<HTMLInputElement>, i)
              }
              onPaste={handlePaste}
              className={styles.otpInput}
              autoFocus={i === 0}
            />
          ))}
        </div>
        {error && (
          <div className={cn(styles.errorText)}>
            {error.otp || error.message}
          </div>
        )}
      </div>
      <div className={cn(styles.submit, styles.mb4)}>
        <button
          className="customBtn greenBtn"
          onClick={handleSubmit}
          disabled={loading === "pending" || otp.join("").length !== length}
        >
          {loading === "pending" ? "Verifying..." : "Submit"}
        </button>
      </div>
      <div className={styles.resendCode}>
        Didn't get the code?{" "}
        <button
          className="linkButton"
          onClick={handleResend}
          disabled={isTimerActive}
        >
          Resend
        </button>
      </div>
    </AuthLayout>
  );
};

export default VerifyOTP;
